/**
 * F-02E-1 — ShareLinkManagerPanel
 *
 * Authenticated owner/operator panel for managing view-only share links on
 * the currently-open project/site.
 *
 *   - Lists all links for `siteId` (most recent first).
 *   - Create button opens the ShareLinkCreateModal.
 *   - Per-row Revoke + Delete with native confirm() guard (matches the
 *     TeamPanel idiom; can be swapped for the uiStore confirm dialog later).
 *   - Renders ONLY owner-safe fields: label, status, expires_at, exposed
 *     sections, has_passphrase, view_count, last_viewed_at, created_at,
 *     token_prefix. Never renders token_hash, passphrase_hash, or the raw
 *     token (those aren't even on `ShareLinkSummary`).
 *   - Expired/revoked rows are visually muted; only `active` (not
 *     derived-expired) links show the Revoke button.
 *   - F-02E-4: expired rows offer Reactivate — an inline expiry editor that
 *     PATCHes a new expires_at (or never_expires) so the SAME URL works
 *     again. Revoked links stay terminal (the backend 409s any update).
 *   - F-02E-5: non-revoked rows offer Edit sections — an inline checkbox
 *     editor that PATCHes `exposedSections` so an existing URL can expose
 *     more (or fewer) sections without being re-minted. The share cookie
 *     carries no section list; the API re-reads the row per request, so
 *     visitors pick the change up on their next page load.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listShareLinks,
  revokeShareLink,
  deleteShareLink,
  copyShareLinkUrl,
  regenerateShareLink,
  updateShareLink,
  buildShareUrlFromPath,
  SHARE_LINK_DEFAULT_EXPIRY_DAYS,
  SHARE_LINK_MAX_EXPIRY_DAYS,
  SHARE_LINK_EXPOSED_SECTION_OPTIONS,
  SHARE_LINK_SECTION_LABELS,
  type ShareLinkExposedSection,
  type ShareLinkSummary,
  type ShareLinkCreated,
  type UpdateShareLinkPayload,
} from '../../services/shareApi';
import { ApiError } from '../../services/apiFetch';
import ShareLinkCreateModal from './ShareLinkCreateModal';
import './ShareLinkManagerPanel.css';

/** F-02E-3C: shown when a legacy link has no recoverable token. */
const LEGACY_UNCOPYABLE_MESSAGE =
  'This older share link cannot be copied. Regenerate it to create a copyable URL.';

/** F-02E-4: reactivate-editor day presets (mirrors ShareLinkCreateModal). */
const EXPIRY_DAY_PRESETS = [7, 30, 60, 90] as const;

/** F-02E-4: warning when reactivating into never-expire mode (mirrors modal). */
const NEVER_EXPIRE_WARNING =
  'This link never expires. Anyone with the URL keeps read-only access ' +
  'indefinitely until you revoke it. Prefer a passphrase and revoke when ' +
  'no longer needed.';

/**
 * F-02E-5: keep only the sections this UI can toggle. The API allowlist is
 * the same five values, so nothing is dropped in practice; if the lists
 * ever drift, an unknown stored value would fail the PATCH schema anyway.
 */
function knownSections(
  sections: readonly string[],
): Set<ShareLinkExposedSection> {
  const known: ReadonlySet<string> = new Set(SHARE_LINK_EXPOSED_SECTION_OPTIONS);
  return new Set(
    sections.filter((s): s is ShareLinkExposedSection => known.has(s)),
  );
}

function sameSectionSet(
  a: ReadonlySet<string>,
  b: ReadonlySet<string>,
): boolean {
  if (a.size !== b.size) return false;
  for (const s of a) if (!b.has(s)) return false;
  return true;
}

interface Props {
  /** Current project's site id. If null/empty, the panel renders a disabled hint. */
  siteId: string | null | undefined;
  /** Optional display name for the site. */
  siteName?: string | null;
  onClose: () => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function StatusBadge({ link }: { link: ShareLinkSummary }) {
  const variant =
    link.derived_status === 'active'
      ? 'active'
      : link.derived_status === 'expired'
      ? 'expired'
      : 'revoked';
  const label =
    variant === 'active'
      ? 'Active'
      : variant === 'expired'
      ? 'Expired'
      : 'Revoked';
  return (
    <span
      className={`share-manager__badge share-manager__badge--${variant}`}
      data-testid={`share-manager-badge-${link.id}`}
    >
      {label}
    </span>
  );
}

export default function ShareLinkManagerPanel({ siteId, siteName, onClose }: Props) {
  const [links, setLinks] = useState<ShareLinkSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowPending, setRowPending] = useState<Record<string, boolean>>({});
  const [createOpen, setCreateOpen] = useState(false);
  // The just-created link (raw URL info) is also kept here so the manager
  // can subtly highlight it in the list immediately after the modal closes.
  const [recentCreatedId, setRecentCreatedId] = useState<string | null>(null);
  // F-02E-3C: per-row Copy URL feedback.
  const [copyState, setCopyState] = useState<
    Record<string, 'idle' | 'copied' | 'error'>
  >({});
  // F-02E-3C: the just-regenerated link, revealed ONCE with a Copy action
  // (the raw token only comes back from the regenerate response).
  const [regenerated, setRegenerated] = useState<ShareLinkCreated | null>(null);
  const [regenCopyState, setRegenCopyState] = useState<
    'idle' | 'copied' | 'error'
  >('idle');
  // F-02E-3C: ref to the just-regenerated row so we can bring its inline
  // reveal into view even when the row sits far down the list.
  const regenRowRef = useRef<HTMLLIElement | null>(null);
  // F-02E-4: which expired row has its inline reactivate editor open
  // (single editor at a time), plus the editor's expiry choice.
  const [reactivateId, setReactivateId] = useState<string | null>(null);
  const [reactivateDays, setReactivateDays] = useState<number>(
    SHARE_LINK_DEFAULT_EXPIRY_DAYS,
  );
  const [reactivateNever, setReactivateNever] = useState(false);
  // F-02E-5: which row has its inline exposed-sections editor open (single
  // editor at a time), plus the draft selection being edited.
  const [sectionsEditId, setSectionsEditId] = useState<string | null>(null);
  const [sectionsDraft, setSectionsDraft] = useState<
    Set<ShareLinkExposedSection>
  >(() => new Set());

  useEffect(() => {
    const el = regenRowRef.current;
    if (regenerated && el && typeof el.scrollIntoView === 'function') {
      try {
        el.scrollIntoView({ block: 'nearest' });
      } catch {
        /* jsdom / unsupported environments: scrollIntoView is a no-op */
      }
    }
  }, [regenerated]);

  const refresh = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listShareLinks(siteId);
      setLinks(data);
      // F-02E-3C: drop a stale regenerate reveal if its link no longer exists
      // (e.g. it was just deleted) so no orphaned "New URL ready" prompt lingers.
      setRegenerated((prev) =>
        prev && data.some((l) => l.id === prev.id) ? prev : null,
      );
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Failed to load share links');
      } else {
        setError('Failed to load share links');
      }
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (siteId) void refresh();
  }, [siteId, refresh]);

  const handleRevoke = async (link: ShareLinkSummary) => {
    const confirmed = window.confirm(
      `Revoke share link "${link.label || link.token_prefix}"?\n\n` +
        'Visitors using this URL will lose access immediately.',
    );
    if (!confirmed) return;
    setRowPending((p) => ({ ...p, [link.id]: true }));
    setError(null);
    try {
      await revokeShareLink(link.id);
      // F-02E-3C: a revoked link's regenerated URL is dead — clear any reveal.
      setRegenerated((prev) => (prev?.id === link.id ? null : prev));
      await refresh();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Failed to revoke share link',
      );
    } finally {
      setRowPending((p) => {
        const next = { ...p };
        delete next[link.id];
        return next;
      });
    }
  };

  const handleDelete = async (link: ShareLinkSummary) => {
    const confirmed = window.confirm(
      `Delete share link "${link.label || link.token_prefix}" permanently?\n\n` +
        'This cannot be undone. (Audit history is preserved.)',
    );
    if (!confirmed) return;
    setRowPending((p) => ({ ...p, [link.id]: true }));
    setError(null);
    try {
      await deleteShareLink(link.id);
      if (recentCreatedId === link.id) setRecentCreatedId(null);
      // F-02E-3C: clear any regenerate reveal tied to the deleted link.
      setRegenerated((prev) => (prev?.id === link.id ? null : prev));
      await refresh();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Failed to delete share link',
      );
    } finally {
      setRowPending((p) => {
        const next = { ...p };
        delete next[link.id];
        return next;
      });
    }
  };

  const handleCreated = (created: ShareLinkCreated) => {
    setRecentCreatedId(created.id);
    void refresh();
  };

  const handleCopyUrl = async (link: ShareLinkSummary) => {
    setError(null);
    try {
      const res = await copyShareLinkUrl(link.id);
      const url = buildShareUrlFromPath(res.share_path);
      try {
        await navigator.clipboard.writeText(url);
        setCopyState((p) => ({ ...p, [link.id]: 'copied' }));
        setTimeout(
          () => setCopyState((p) => ({ ...p, [link.id]: 'idle' })),
          1800,
        );
      } catch {
        setCopyState((p) => ({ ...p, [link.id]: 'error' }));
      }
    } catch (err) {
      // State may have drifted (revoked / expired / legacy) since the list
      // was loaded — refresh so the row reflects reality, THEN surface the
      // error. (refresh() resets `error`, so the message must be set after.)
      await refresh();
      if (err instanceof ApiError) {
        setError(
          err.code === 'SHARE_LINK_TOKEN_UNAVAILABLE'
            ? LEGACY_UNCOPYABLE_MESSAGE
            : err.message || 'Could not copy the share URL',
        );
      } else {
        setError('Could not copy the share URL');
      }
    }
  };

  const handleRegenerate = async (link: ShareLinkSummary) => {
    const confirmed = window.confirm(
      `Regenerate share link "${link.label || link.token_prefix}"?\n\n` +
        'The current URL will stop working immediately. A new URL will be ' +
        'created — anyone using the old URL will lose access.',
    );
    if (!confirmed) return;
    setRowPending((p) => ({ ...p, [link.id]: true }));
    setError(null);
    try {
      const result = await regenerateShareLink(link.id);
      setRegenerated(result);
      setRegenCopyState('idle');
      // Reuse the existing recent-row highlight to draw the eye to the row
      // whose inline reveal just appeared.
      setRecentCreatedId(result.id);
      await refresh();
    } catch (err) {
      // refresh() resets `error`; set the message after it so it persists.
      await refresh();
      setError(
        err instanceof ApiError
          ? err.message
          : 'Failed to regenerate share link',
      );
    } finally {
      setRowPending((p) => {
        const next = { ...p };
        delete next[link.id];
        return next;
      });
    }
  };

  // F-02E-4: open the inline reactivate editor for one expired row,
  // resetting the expiry choice to defaults each time.
  const openReactivate = (link: ShareLinkSummary) => {
    // One inline editor at a time — close a sections editor if open.
    setSectionsEditId(null);
    setReactivateId(link.id);
    setReactivateDays(SHARE_LINK_DEFAULT_EXPIRY_DAYS);
    setReactivateNever(false);
  };

  const handleReactivate = async (link: ShareLinkSummary) => {
    setRowPending((p) => ({ ...p, [link.id]: true }));
    setError(null);
    try {
      // Field links must keep a finite expiry (the backend rejects
      // never-expire for kind='field'); the checkbox is hidden for them,
      // and this guard keeps the payload honest even if state drifts.
      const payload: UpdateShareLinkPayload =
        reactivateNever && link.kind !== 'field'
          ? { neverExpires: true }
          : {
              expiresAt: new Date(
                Date.now() + reactivateDays * 24 * 60 * 60 * 1000,
              ).toISOString(),
            };
      await updateShareLink(link.id, payload);
      setReactivateId(null);
      await refresh();
    } catch (err) {
      // State may have drifted (e.g. revoked elsewhere → 409) — refresh so
      // the row reflects reality, THEN surface the error (refresh() resets
      // `error`, so the message must be set after).
      await refresh();
      setError(
        err instanceof ApiError
          ? err.message
          : 'Failed to reactivate share link',
      );
    } finally {
      setRowPending((p) => {
        const next = { ...p };
        delete next[link.id];
        return next;
      });
    }
  };

  // F-02E-5: open the inline exposed-sections editor for one row, seeding
  // the draft from the row's current sections. Closes the reactivate
  // editor so only one inline editor is open at a time.
  const openSectionsEditor = (link: ShareLinkSummary) => {
    setReactivateId(null);
    setSectionsEditId(link.id);
    setSectionsDraft(knownSections(link.exposed_sections));
  };

  const toggleDraftSection = (section: ShareLinkExposedSection) => {
    setSectionsDraft((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const handleSaveSections = async (link: ShareLinkSummary) => {
    setRowPending((p) => ({ ...p, [link.id]: true }));
    setError(null);
    try {
      // Canonical order (the option list) so the PATCH body is stable
      // regardless of click order. The body REPLACES the stored list.
      const exposedSections = SHARE_LINK_EXPOSED_SECTION_OPTIONS.filter((s) =>
        sectionsDraft.has(s),
      );
      const updated = await updateShareLink(link.id, { exposedSections });
      // Apply the PATCH response to the row immediately so the meta line
      // never flashes the old list between the editor closing and the
      // refresh landing (the response is the freshly re-read row).
      setLinks((prev) =>
        prev ? prev.map((l) => (l.id === updated.id ? updated : l)) : prev,
      );
      setSectionsEditId(null);
      await refresh();
    } catch (err) {
      // State may have drifted (e.g. revoked elsewhere → 409) — refresh so
      // the row reflects reality, THEN surface the error (refresh() resets
      // `error`, so the message must be set after).
      await refresh();
      setError(
        err instanceof ApiError
          ? err.message
          : 'Failed to update exposed sections',
      );
    } finally {
      setRowPending((p) => {
        const next = { ...p };
        delete next[link.id];
        return next;
      });
    }
  };

  const handleCopyRegenerated = async () => {
    if (!regenerated) return;
    const url = buildShareUrlFromPath(regenerated.share_path);
    try {
      await navigator.clipboard.writeText(url);
      setRegenCopyState('copied');
      setTimeout(() => setRegenCopyState('idle'), 1800);
    } catch {
      setRegenCopyState('error');
    }
  };

  return (
    <div className="share-manager" data-testid="share-manager-panel">
      <header className="share-manager__header">
        <span className="share-manager__title">
          Share links
          {siteName && <span className="share-manager__site">· {siteName}</span>}
        </span>
        <button
          type="button"
          className="share-manager__header-btn"
          onClick={onClose}
          aria-label="Close share-links panel"
        >
          ×
        </button>
      </header>

      <div className="share-manager__body">
        {!siteId && (
          <div className="share-manager__placeholder" data-testid="share-manager-no-site">
            Open a project to manage its view-only share links.
          </div>
        )}

        {siteId && (
          <>
            <div className="share-manager__toolbar">
              <button
                type="button"
                className="share-manager__primary"
                onClick={() => setCreateOpen(true)}
                disabled={loading}
                data-testid="share-manager-create"
              >
                + Create share link
              </button>
              <button
                type="button"
                className="share-manager__ghost"
                onClick={() => void refresh()}
                disabled={loading}
              >
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            <p className="share-manager__hint">
              Share links are public, read-only snapshots. The raw URL is
              shown <strong>only once</strong> at creation. Revoking
              invalidates the link immediately; deleting also removes it
              from this list. Expired links can be reactivated — the same
              URL starts working again. Exposed sections can be changed at
              any time without changing the URL.
            </p>

            {error && (
              <div
                className="share-manager__alert share-manager__alert--error"
                role="alert"
                data-testid="share-manager-error"
              >
                {error}
              </div>
            )}

            {loading && !links && (
              <div className="share-manager__placeholder" data-testid="share-manager-loading">
                Loading share links…
              </div>
            )}

            {links && links.length === 0 && (
              <div className="share-manager__placeholder" data-testid="share-manager-empty">
                No share links yet for this project. Create one above to
                start sharing.
              </div>
            )}

            {links && links.length > 0 && (
              <ul className="share-manager__list" data-testid="share-manager-list">
                {links.map((link) => {
                  const pending = !!rowPending[link.id];
                  const isActive = link.derived_status === 'active';
                  const isExpired = link.derived_status === 'expired';
                  const isRevoked = link.derived_status === 'revoked';
                  const muted = !isActive;
                  // F-02E-5: Save is a no-op (and disabled) until the draft
                  // differs from what the row already exposes.
                  const sectionsUnchanged =
                    sectionsEditId === link.id &&
                    sameSectionSet(
                      sectionsDraft,
                      knownSections(link.exposed_sections),
                    );
                  return (
                    <li
                      key={link.id}
                      ref={regenerated?.id === link.id ? regenRowRef : undefined}
                      className={
                        'share-manager__item' +
                        (muted ? ' share-manager__item--muted' : '') +
                        (recentCreatedId === link.id
                          ? ' share-manager__item--recent'
                          : '')
                      }
                      data-testid={`share-manager-item-${link.id}`}
                    >
                      <div className="share-manager__item-row share-manager__item-row--head">
                        <div className="share-manager__item-label">
                          {link.label || (
                            <span className="share-manager__item-label--placeholder">
                              (unlabeled)
                            </span>
                          )}
                          {/* FL-1: writable links are visually unmistakable. */}
                          {link.kind === 'field' && (
                            <span
                              className="share-manager__kind-badge"
                              data-testid={`share-manager-kind-${link.id}`}
                              title="Field link — holders can update indication notes and status"
                            >
                              FIELD
                            </span>
                          )}
                        </div>
                        <StatusBadge link={link} />
                      </div>

                      <dl className="share-manager__meta">
                        <div>
                          <dt>Expires</dt>
                          <dd data-testid={`share-manager-expiry-${link.id}`}>
                            {link.never_expires
                              ? 'Never'
                              : formatDate(link.expires_at)}
                          </dd>
                        </div>
                        <div>
                          <dt>Created</dt>
                          <dd>{formatDate(link.created_at)}</dd>
                        </div>
                        <div>
                          <dt>Views</dt>
                          <dd>{link.view_count}</dd>
                        </div>
                        <div>
                          <dt>Last viewed</dt>
                          <dd>{formatDate(link.last_viewed_at)}</dd>
                        </div>
                        <div>
                          <dt>Passphrase</dt>
                          <dd>{link.has_passphrase ? 'Required' : 'None'}</dd>
                        </div>
                        <div>
                          <dt>Exposed sections</dt>
                          <dd>
                            {link.exposed_sections.length
                              ? link.exposed_sections.join(', ')
                              : 'Core only'}
                          </dd>
                        </div>
                        <div>
                          <dt>Token</dt>
                          <dd>
                            <code className="share-manager__token-prefix">
                              {link.token_prefix}…
                            </code>
                          </dd>
                        </div>
                      </dl>

                      {isActive && link.requires_regenerate && (
                        <div
                          className="share-manager__legacy-note"
                          data-testid={`share-manager-legacy-${link.id}`}
                        >
                          {LEGACY_UNCOPYABLE_MESSAGE}
                        </div>
                      )}

                      <div className="share-manager__item-actions">
                        {isActive && link.copy_available && (
                          <button
                            type="button"
                            className="share-manager__ghost"
                            onClick={() => void handleCopyUrl(link)}
                            disabled={pending}
                            data-testid={`share-manager-copy-${link.id}`}
                          >
                            {copyState[link.id] === 'copied'
                              ? 'Copied!'
                              : copyState[link.id] === 'error'
                              ? 'Copy failed'
                              : 'Copy URL'}
                          </button>
                        )}
                        {isActive && (
                          <button
                            type="button"
                            className="share-manager__ghost"
                            onClick={() => void handleRegenerate(link)}
                            disabled={pending}
                            data-testid={`share-manager-regenerate-${link.id}`}
                          >
                            {pending
                              ? '…'
                              : link.requires_regenerate
                              ? 'Regenerate to copy'
                              : 'Regenerate'}
                          </button>
                        )}
                        {isActive && (
                          <button
                            type="button"
                            className="share-manager__ghost"
                            onClick={() => void handleRevoke(link)}
                            disabled={pending}
                            data-testid={`share-manager-revoke-${link.id}`}
                          >
                            {pending ? '…' : 'Revoke'}
                          </button>
                        )}
                        {/* F-02E-4: only derived-expired rows are revivable —
                            revoked links are terminal by design. */}
                        {isExpired && (
                          <button
                            type="button"
                            className="share-manager__ghost"
                            onClick={() =>
                              reactivateId === link.id
                                ? setReactivateId(null)
                                : openReactivate(link)
                            }
                            disabled={pending}
                            data-testid={`share-manager-reactivate-${link.id}`}
                          >
                            {pending ? '…' : 'Reactivate'}
                          </button>
                        )}
                        {/* F-02E-5: sections are editable in place on any
                            non-revoked row — the URL never changes. Revoked
                            links are terminal (backend 409s the PATCH). */}
                        {!isRevoked && (
                          <button
                            type="button"
                            className="share-manager__ghost"
                            onClick={() =>
                              sectionsEditId === link.id
                                ? setSectionsEditId(null)
                                : openSectionsEditor(link)
                            }
                            disabled={pending}
                            data-testid={`share-manager-sections-${link.id}`}
                          >
                            {pending ? '…' : 'Edit sections'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="share-manager__danger"
                          onClick={() => void handleDelete(link)}
                          disabled={pending}
                          data-testid={`share-manager-delete-${link.id}`}
                        >
                          {pending ? '…' : 'Delete'}
                        </button>
                      </div>

                      {isExpired && reactivateId === link.id && (
                        <div
                          className="share-manager__reactivate"
                          data-testid={`share-manager-reactivate-editor-${link.id}`}
                        >
                          <div className="share-manager__reactivate-title">
                            Reactivate this link — the <strong>same URL</strong>{' '}
                            starts working again for anyone who already has it.
                          </div>

                          {link.kind !== 'field' && (
                            <label className="share-manager__never-expire">
                              <input
                                type="checkbox"
                                checked={reactivateNever}
                                onChange={(e) =>
                                  setReactivateNever(e.target.checked)
                                }
                                data-testid={`share-manager-reactivate-never-${link.id}`}
                              />
                              <span>Never expires</span>
                            </label>
                          )}

                          {reactivateNever && link.kind !== 'field' ? (
                            <div
                              className="share-manager__alert share-manager__alert--warn"
                              role="alert"
                              data-testid={`share-manager-reactivate-warning-${link.id}`}
                            >
                              {NEVER_EXPIRE_WARNING}
                            </div>
                          ) : (
                            <div
                              className="share-manager__reactivate-days"
                              role="radiogroup"
                              aria-label="New expiry in days"
                            >
                              {EXPIRY_DAY_PRESETS.map((days) => (
                                <button
                                  key={days}
                                  type="button"
                                  role="radio"
                                  aria-checked={reactivateDays === days}
                                  className={
                                    'share-manager__chip' +
                                    (reactivateDays === days
                                      ? ' share-manager__chip--active'
                                      : '')
                                  }
                                  onClick={() => setReactivateDays(days)}
                                  data-testid={`share-manager-reactivate-days-${days}-${link.id}`}
                                >
                                  {days} days
                                </button>
                              ))}
                              <input
                                type="number"
                                className="share-manager__days-input"
                                min={1}
                                max={SHARE_LINK_MAX_EXPIRY_DAYS}
                                value={reactivateDays}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10);
                                  if (!Number.isFinite(v)) return;
                                  setReactivateDays(
                                    Math.max(
                                      1,
                                      Math.min(SHARE_LINK_MAX_EXPIRY_DAYS, v),
                                    ),
                                  );
                                }}
                                aria-label="Custom days"
                                data-testid={`share-manager-reactivate-custom-${link.id}`}
                              />
                            </div>
                          )}

                          <div className="share-manager__reactivate-actions">
                            <button
                              type="button"
                              className="share-manager__ghost"
                              onClick={() => setReactivateId(null)}
                              disabled={pending}
                              data-testid={`share-manager-reactivate-cancel-${link.id}`}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="share-manager__primary"
                              onClick={() => void handleReactivate(link)}
                              disabled={pending}
                              data-testid={`share-manager-reactivate-confirm-${link.id}`}
                            >
                              {pending
                                ? '…'
                                : reactivateNever && link.kind !== 'field'
                                ? 'Reactivate — never expires'
                                : `Reactivate for ${reactivateDays} days`}
                            </button>
                          </div>
                        </div>
                      )}

                      {!isRevoked && sectionsEditId === link.id && (
                        <div
                          className="share-manager__sections"
                          data-testid={`share-manager-sections-editor-${link.id}`}
                        >
                          <div className="share-manager__sections-title">
                            Choose what this link exposes. The{' '}
                            <strong>URL stays the same</strong> — visitors see
                            the change the next time they open it. Core (map,
                            orthophotos, indications) is always included.
                          </div>

                          <div
                            className="share-manager__sections-grid"
                            role="group"
                            aria-label="Exposed sections"
                          >
                            {SHARE_LINK_EXPOSED_SECTION_OPTIONS.map((section) => {
                              const checked = sectionsDraft.has(section);
                              return (
                                <label
                                  key={section}
                                  className={
                                    'share-manager__section-option' +
                                    (checked
                                      ? ' share-manager__section-option--checked'
                                      : '')
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleDraftSection(section)}
                                    disabled={pending}
                                    aria-label={SHARE_LINK_SECTION_LABELS[section]}
                                    data-testid={`share-manager-section-${section}-${link.id}`}
                                  />
                                  <span>{SHARE_LINK_SECTION_LABELS[section]}</span>
                                </label>
                              );
                            })}
                          </div>

                          <div className="share-manager__sections-actions">
                            <button
                              type="button"
                              className="share-manager__ghost"
                              onClick={() => setSectionsEditId(null)}
                              disabled={pending}
                              data-testid={`share-manager-sections-cancel-${link.id}`}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="share-manager__primary"
                              onClick={() => void handleSaveSections(link)}
                              disabled={pending || sectionsUnchanged}
                              data-testid={`share-manager-sections-save-${link.id}`}
                            >
                              {pending
                                ? '…'
                                : sectionsUnchanged
                                ? 'No changes'
                                : 'Save sections'}
                            </button>
                          </div>
                        </div>
                      )}

                      {isActive &&
                        regenerated &&
                        regenerated.id === link.id && (
                          <div
                            className="share-manager__regenerated"
                            data-testid={`share-manager-regenerated-${link.id}`}
                          >
                            <div className="share-manager__regenerated-title">
                              New URL ready — <strong>copy it now</strong>. The
                              old URL no longer works.
                            </div>
                            <div className="share-manager__url-row">
                              <input
                                type="text"
                                className="share-manager__url-input"
                                readOnly
                                value={buildShareUrlFromPath(
                                  regenerated.share_path,
                                )}
                                onFocus={(e) => e.currentTarget.select()}
                                data-testid={`share-manager-regenerated-url-${link.id}`}
                                aria-label="Regenerated share URL"
                              />
                              <button
                                type="button"
                                className="share-manager__primary"
                                onClick={() => void handleCopyRegenerated()}
                                data-testid={`share-manager-regenerated-copy-${link.id}`}
                              >
                                {regenCopyState === 'copied'
                                  ? 'Copied!'
                                  : regenCopyState === 'error'
                                  ? 'Copy failed'
                                  : 'Copy URL'}
                              </button>
                              <button
                                type="button"
                                className="share-manager__ghost"
                                onClick={() => setRegenerated(null)}
                                aria-label="Dismiss new URL"
                                data-testid={`share-manager-regenerated-dismiss-${link.id}`}
                              >
                                Dismiss
                              </button>
                            </div>
                          </div>
                        )}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>

      {createOpen && siteId && (
        <ShareLinkCreateModal
          siteId={siteId}
          siteName={siteName ?? null}
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
